import React from 'react';
import { grey, red } from '@mui/material/colors';
import { InputField } from 'components/form';
import { useFormContext, useWatch } from 'react-hook-form';
import { ListingForm } from 'components/shared/TableListing';
import { ActionsMenu } from 'components/shared/Menu/ActionsMenu';
import { ArrowDropDown, DeleteOutline } from '@mui/icons-material';
import { Box, capitalize, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { StagesFormValues } from '../../../../../types';
import { AttributeRequiredIndicator } from './AttributeRequiredIndicator';

interface Props {
  rowIndex: number;
  isEdit: boolean;
  disabled: boolean;
  attributeIndex: number;
  onRemove: VoidFunction;
  prefix: keyof Omit<StagesFormValues, 'name' | 'id'>;
}

export const AttributeCellFields = ({ rowIndex, attributeIndex, isEdit, prefix, onRemove, disabled }: Props) => {
  const { control, setValue } = useFormContext<ListingForm<StagesFormValues>>();
  const isRequired = useWatch({
    control,
    name: `bodyFields.${rowIndex}.${prefix}.${attributeIndex}.isRequired`,
  });

  const handleUpdateIsRequired = (value: boolean) => {
    setValue(`bodyFields.${rowIndex}.${prefix}.${attributeIndex}.isRequired`, value);
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Stack direction="row" gap="0.5rem" width="100%" alignItems="center">
        <InputField
          control={control}
          name={`bodyFields.${rowIndex}.${prefix}.${attributeIndex}.name`}
          inputProps={{
            disabled,
            required: true,
            variant: 'outlined',
            placeholder: `${capitalize(prefix)} field name`,
          }}
        />

        {!isEdit && (
          <Tooltip title="Remove configuration">
            <IconButton
              size="small"
              color="error"
              onClick={onRemove}
              disabled={disabled}
              sx={{ alignSelf: 'flex-start' }}
            >
              <DeleteOutline />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" alignItems="center">
        <Stack alignItems="center" justifyContent="center" mr="0.5rem">
          <AttributeRequiredIndicator color={isRequired ? red[500] : grey[200]} />
        </Stack>

        <Typography variant="caption">{isRequired ? 'Required' : 'Optional'}</Typography>

        <ActionsMenu
          renderTrigger={({ onClick }) => (
            <IconButton onClick={onClick} size="small">
              <ArrowDropDown sx={{ color: 'black' }} fontSize="small" />
            </IconButton>
          )}
          config={[
            {
              disabled,
              color: grey[500],
              label: 'Optional',
              action: () => handleUpdateIsRequired(false),
              icon: <AttributeRequiredIndicator color={grey[200]} />,
            },
            {
              disabled,
              color: red[500],
              label: 'Required',
              action: () => handleUpdateIsRequired(true),
              icon: <AttributeRequiredIndicator color={red[500]} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
